'use client';

import { useRef } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { Avatar } from '@/components/ui';

export interface YouRowProps {
  /** Collapsed rail renders the avatar alone. */
  collapsed: boolean;
  name: string;
  email: string;
  photoUrl: string | null;
  locale: string;
  onSettings: () => void;
  onSignOut: () => void;
}

const ITEM_CLASS =
  'hover:bg-surface focus:bg-surface cursor-pointer rounded px-3 py-2 text-sm outline-none';

/**
 * The signed-in footer of the sidebar: the player's avatar, name and email, opening a two-item
 * menu (Settings, Sign out). Replaces the theme and language controls and the bare sign-out button.
 */
export function YouRow({ collapsed, name, email, photoUrl, onSettings, onSignOut }: YouRowProps) {
  const t = useTranslations('Nav');
  const display = name || email;
  // The pick runs once the menu has closed: its focus trap would otherwise pull focus back out of
  // a leave dialog the guard opens in the same commit, and drop it on <body>. Escape still returns
  // focus to the trigger.
  const picked = useRef<(() => void) | null>(null);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('youMenu')}
          title={collapsed ? display : t('youMenuFor', { name: display })}
          className={`hover:bg-foreground/[0.06] focus-visible:ring-primary flex cursor-pointer items-center rounded-md text-left transition-colors focus-visible:ring-2 focus-visible:outline-none ${
            collapsed ? 'mx-auto h-10 w-12 justify-center' : 'w-full gap-3 px-3 py-2'
          }`}
        >
          <Avatar name={display} photoUrl={photoUrl} size="sm" />
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="text-foreground block truncate text-sm font-medium">
                  {display}
                </span>
                {name && email && (
                  <span className="text-muted block truncate text-xs">{email}</span>
                )}
              </span>
              <ChevronUpIcon className="text-muted h-4 w-4 shrink-0" aria-hidden="true" />
            </>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="top"
          align={collapsed ? 'start' : 'center'}
          sideOffset={6}
          onCloseAutoFocus={(e) => {
            const run = picked.current;
            if (!run) return;
            picked.current = null;
            e.preventDefault();
            run();
          }}
          className="bg-card border-border text-foreground z-50 min-w-[12rem] rounded-md border p-1 shadow-md"
        >
          <DropdownMenu.Item onSelect={() => (picked.current = onSettings)} className={ITEM_CLASS}>
            {t('settings')}
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="bg-border my-1 h-px" />
          <DropdownMenu.Item onSelect={() => (picked.current = onSignOut)} className={ITEM_CLASS}>
            {t('logout')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
